// A22 — Work Receipts + Gate Evidence. Receipts list with kind
// filter, body preview, supersession chain, and PR-export button.
// Receipts are generated views over evidence; we show them but link
// back to the evidence_refs.

import { useEffect, useState } from "react";
import { TabScrollbox } from "../../chrome/TabScrollbox";
import {
  ApiError,
  listReceipts,
  prExportReceipt,
  type ReceiptRow,
} from "../../api/client";

interface Props {
  workspaceId: number;
}

const KINDS = ["all", "turn", "commit", "gate", "pr", "digest", "meeting"] as const;

export function ReceiptsTab({ workspaceId }: Props) {
  const [kind, setKind] = useState<(typeof KINDS)[number]>("all");
  const [rows, setRows] = useState<ReceiptRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<ReceiptRow | null>(null);
  const [exported, setExported] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    listReceipts(workspaceId, kind === "all" ? undefined : kind, 200)
      .then((rs) => {
        if (!cancelled) setRows(rs);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, kind]);

  const exportSelected = async () => {
    if (!selected) return;
    try {
      const r = await prExportReceipt(workspaceId, selected.receipt_id);
      setExported(r.markdown);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  return (
    <TabScrollbox>
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <p className="max-w-2xl text-xs text-muted">
          Receipts are generated views over the trace — turn, commit, gate,
          PR, digest, meeting. Each is sourced from `evidence_refs_json`.
          Supersede a receipt with a retro note instead of mutating it.
        </p>
        <div className="flex flex-wrap gap-1">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`rounded-md border px-2 py-0.5 text-[0.625rem] ${
                kind === k
                  ? "border-accent text-accent"
                  : "border-edge text-muted hover:border-accent"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
      </header>
      {busy && <p className="text-xs text-muted">Loading receipts…</p>}
      {error && <p className="text-xs text-danger">{error}</p>}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr,minmax(0,1.5fr)]">
        <ul className="flex flex-col gap-2">
          {rows.length === 0 && !busy && (
            <li className="text-xs text-faint">No receipts yet.</li>
          )}
          {rows.map((r) => {
            const selectedThis = selected?.receipt_id === r.receipt_id;
            return (
              <li key={r.receipt_id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(r);
                    setExported(null);
                  }}
                  className={`w-full rounded-md border bg-surface-raised px-3 py-2 text-left transition-colors ${
                    selectedThis
                      ? "border-accent"
                      : "border-edge hover:border-accent"
                  }`}
                  data-testid={`receipt-row-${r.receipt_id}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-primary">
                      {r.title}
                    </span>
                    <span className="font-code text-[0.625rem] text-faint">
                      {r.kind}
                    </span>
                  </div>
                  <p className="mt-1 line-clamp-2 text-[0.625rem] text-muted">
                    {r.body_md.replace(/^#+\s*/gm, "").slice(0, 200)}
                  </p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="font-code text-[0.625rem] text-faint">
                      {new Date(r.created_at / 1000).toLocaleString()}
                    </span>
                    {r.superseded_by != null && (
                      <span className="font-code text-[0.625rem] text-warning">
                        superseded → {r.superseded_by}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <aside className="rounded-md border border-edge bg-surface p-3">
          {selected ? (
            <>
              <header className="flex items-baseline justify-between gap-2">
                <h3 className="text-xs font-semibold text-primary">
                  {selected.title}
                </h3>
                <button
                  type="button"
                  onClick={() => void exportSelected()}
                  className="rounded-md border border-accent px-2 py-0.5 text-[0.625rem] font-medium text-accent hover:bg-accent/10"
                  data-testid="receipt-export-pr"
                >
                  Export as PR markdown
                </button>
              </header>
              <pre className="mt-3 max-h-[60vh] overflow-auto whitespace-pre-wrap text-xs text-primary">
                {selected.body_md}
              </pre>
              <details className="mt-3">
                <summary className="cursor-pointer text-[0.625rem] text-muted hover:text-primary">
                  evidence_refs
                </summary>
                <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap font-code text-[0.625rem] text-muted">
                  {selected.evidence_refs_json}
                </pre>
              </details>
              {exported !== null && (
                <details className="mt-3" open>
                  <summary className="cursor-pointer text-[0.625rem] text-muted hover:text-primary">
                    PR markdown (copy below)
                  </summary>
                  <textarea
                    readOnly
                    value={exported}
                    className="mt-1 w-full rounded-md border border-edge bg-surface px-2 py-1 font-code text-[0.625rem] text-primary"
                    rows={12}
                  />
                </details>
              )}
            </>
          ) : (
            <p className="text-xs text-muted">Select a receipt to read.</p>
          )}
        </aside>
      </div>
    </TabScrollbox>
  );
}
